'use client'
import React, { FC } from 'react'
import { TimelineEventPrefix } from './partials/TimelineEventPrefix'
import { File } from '../../../types'
import { FileCard } from '../../atoms/Card/FileCard'
import { RichTruncate } from '../../atoms/Truncate/RichTruncate'
import { TimelineEventFooter } from './partials/TimelineEventFooter'
import { TimelineEventVariants, TimelineEventVisibility } from './types/TimelineEventTypes'

export interface TimelineEventProps extends Omit<React.HTMLAttributes<HTMLLIElement>, 'prefix'> {
    /** The text of the event. Can contain links etc but will be wrapped in a <Text> component (so a <p> element) */
    children: React.ReactNode
    /** Formatted date. The date should be formatted already when passed in because the internal "dayjs" doesn't know about the correct locale */
    formattedDate?: string
    /** Formatted name. The "getFullName" from the "helpers" can be used to correctly format a name to pass it as "actorName" or a custom calue for "system" messages can be used */
    actorName?: string
    /** Possible popover for the actor. When provided, "actorName" will be ignored */
    actorPopover?: React.ReactElement
    /** Name of the company who triggered the event */
    companyName?: string
    /** Possible popover for the company. When provided, "companyName" will be ignored */
    companyPopover?: React.ReactElement
    /** Can pass in an Avatar or Icon to replace the default "Dot" */
    prefix?: React.ReactElement
    /** Allows to set the variant of the event, i.e. "Success" */
    variant?: TimelineEventVariants
    /** When publicly visible, it adds an "eye" icon to indicate the public visibility */
    visibility?: TimelineEventVisibility
    /** Optionally a file can be provided to show a "FileCard" */
    file?: File
    /** When set to "true", actorName/actorProvider & companyName/companyProvider won't be used. Instead, a common translation "STO.COMPONENTS.COMMON.SYSTEM" is used */
    isSystemEvent?: boolean
    /** Automatically added when used as a child in "Timeline" component */
    isFirst?: boolean
    /** Automatically added when used as a child in "Timeline" component */
    isLast?: boolean
}

export const TimelineEvent: FC<TimelineEventProps> = ({
    children,
    formattedDate,
    actorName,
    actorPopover,
    companyName,
    companyPopover,
    prefix,
    variant = TimelineEventVariants.NONE,
    visibility,
    file,
    isSystemEvent = false,
    isFirst,
    isLast,
    ...props
}) => {
    const rootClass = 'timeline-item'

    const getRootClasses = (): string => {
        let classes = ''
        if (variant && variant !== 'none') classes += `${rootClass}--${variant} ${rootClass}--has-variant`
        if (isFirst) classes += ` ${rootClass}--first`
        if (isLast) classes += ` ${rootClass}--last`
        classes += ` ${props.className ?? ''}`
        return classes
    }

    return (
        <li {...props} className={`${rootClass} ${getRootClasses()}`} data-testid="timeline-event">
            <span className={`${rootClass}__line`} />
            <div className={`${rootClass}__content`}>
                <TimelineEventPrefix prefix={prefix} />
                <div className="flex flex-col grow">
                    <RichTruncate hasShowMore lines={3}>
                        {children}
                    </RichTruncate>
                    <TimelineEventFooter
                        formattedDate={formattedDate}
                        actorName={actorName}
                        actorPopover={actorPopover}
                        companyName={companyName}
                        companyPopover={companyPopover}
                        visibility={visibility}
                        isSystemEvent={isSystemEvent}
                    />
                </div>
                {file && <FileCard file={file} className="mt-2" />}
            </div>
        </li>
    )
}
